<template>
  <div aria-hidden="true">
    <div v-if="showDragInfo || files.length" class="drag-info">
      <!--      Preview of single file or list of files -->
      <SModal
        v-model="showUploadFileModal"
        :header-title="$t('chats.upload.file_upload')"
        :show-footer-gradient="false"
        modal-class=""
        centered
        @hidden="cancelUpload"
      >
        <div v-if="showUploadFileModal">
          <SList class="upload-list">
            <SListItem
              v-for="(item, i) in files"
              :key="`upload-item-${i}`"
              :title="item.name"
              :description="$getSize(item.size, false)"
              :image="item.can_show_preview ? item.value : ''"
              :icon="!item.can_show_preview ? 'icon-file-o' : ''"
              :badges="item.alt_name ? [{ text: $t('ui.alternative_text.modal.title'), color: 'secondary', testId: 'alt-text' }] : []"
            >
              <template #right-action>
                <SDropDown id="options-modal"
                           right
                           no-caret
                           class="add-dropdown"
                           variant="link"
                           data-test="file-upload-dropdown"
                >
                  <template #button-content>
                    <sdui-icon class="icon-more gray-300" variant="gray-300" />
                    <span class="sr-only">{{ $t('ui.file_upload.dropdown_action') }}</span>
                  </template>

                  <template v-if="isImage(item)">
                    <b-dropdown-item-button data-test="dropdown-option-add-alt-text" @click="openAltTextModal(item.id)">
                      <sdui-icon class="icon-note-o primary" />
                      <span class="ml-2 d-inline-block align-middle">{{
                        item.alt_name ? $t('ui.alternative_text.edit') : $t('ui.alternative_text.add')
                      }}</span>
                    </b-dropdown-item-button>
                    <li class="dropdown-divider" aria-hidden="true" />
                  </template>

                  <b-dropdown-item-button data-test="dropdown-option-delete" @click="deleteFile(item)">
                    <sdui-icon class="icon-delete-o primary" />
                    <span class="ml-2 d-inline-block align-middle">{{ $t('chats.upload.file_remove') }}</span>
                  </b-dropdown-item-button>
                </SDropDown>
              </template>

              <template #modals>
                <add-edit-alt-text
                  return-focus="#options-modal__BV_toggle_"
                  :show-modal="!!showAltTextModal.some((el) => el === item.id)"
                  :image-preview-src="item.value"
                  :current-alt-text="item.alt_name"
                  @submit="(newAltText) => updateAltText(item.id, newAltText)"
                  @close="closeAltTextModal(item.id)"
                />
              </template>
            </SListItem>
          </SList>
          <small v-if="!isMobile" class="text-muted mt-2 d-block text-center">{{ $t('ui.file_upload.instructions') }}</small>

          <b-button block
                    variant="link"
                    size="lg"
                    class="mt-2 mb-1"
                    @click="uploadAnotherFile"
          >
            {{ $t('ui.file_upload.add_more_files') }}
          </b-button>
        </div>

        <template #modal-footer>
          <b-button variant="primary"
                    block
                    size="lg"
                    data-cy="upload"
                    @click="submitAndClose"
          >
            {{ $t('global.upload') }}
          </b-button>
        </template>
      </SModal>

      <!--      Indicator after dragging/ before your drops files  -->
      <!--eslint-disable-next-line vue/no-restricted-html-elements-->
      <b-modal v-if="showDragInfo"
               id="drop-files-indicator"
               visible
               no-fade
               centered
               hide-footer
               hide-header
               @hidden="cancelUpload"
      >
        <div class="inner-info white-bg drag-wrapper d-flex align-items-center justify-content-around w-100 h-100">
          <div>
            <span class="icon sdui-icon icon-cloud-upload mb-2 d-block text-center fz-giant text-primary" aria-hidden="true" />
            <h2 data-cy="drop-files-instructions">{{ $t('ui.file_upload.drop_files') }}</h2>
          </div>
        </div>
      </b-modal>
    </div>

    <!-- HIDDEN -->
    <label :for="inputId" class="sr-only">{{ $t('chats.upload.file_upload') }}</label>
    <input
      :id="inputId"
      type="file"
      :accept="extension"
      :name="allowMultiFiles"
      hidden
      multiple
      :capture="useCamera"
      @paste="handleFiles"
      @change="handleFiles"
      @ondrop="handleFiles"
    />

    <SModal
      v-if="isAndroid"
      v-model="chooseModal"
      :return-focus="isUploadAnotherFile ? undefined : returnFocusAfterModalClose"
      modal-class="modal--mobile-bottom"
      no-padding
      hide-footer
      :header-title="$t('chats.upload.choose_action')"
      size="sm"
      @hide="chooseModal = false"
    >
      <div v-if="chooseModal" class="file-access__action d-flex justify-content-center">
        <b-button
          class="file-access__button bg-primary-light w-100 d-inline-flex flex-column align-items-center"
          variant="transparent"
          @click="openFileMenu(true)"
        >
          <sdui-icon class="icon-camera primary mb-1" />
          <span class="small primary">{{ $t('chats.upload.camera') }}</span>
        </b-button>
        <b-button
          class="file-access__button bg-primary-light w-100 d-inline-flex flex-column align-items-center"
          variant="transparent"
          @click="openFileMenu(false)"
        >
          <sdui-icon class="icon-media primary mb-1" />
          <span class="small primary">{{ $t('chats.upload.media') }}</span>
        </b-button>
      </div>
    </SModal>
  </div>
</template>

<script>
import isMobileDeviceMixin from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'
import AddEditAltText from 'src/components/miscellaneous/ui/dialogs/AddEditAltText.vue'
import native from 'src/config/native.js'

export default {
  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  name: 'HiddenFileUpload',

  components: {
    AddEditAltText,
  },

  mixins: [isMobileDeviceMixin],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },

    multiFiles: {
      type: Boolean,
      default: true,
    },

    showPreviewBeforeUpload: {
      type: Boolean,
      default: false,
    },

    showDraggingInfo: {
      type: Boolean,
      default: true,
    },

    fileTypes: {
      type: Array,
      default: () => [],
    },

    inputId: {
      type: String,
      default: 'upload-input',
    },

    returnFocusAfterModalClose: {
      type: [String, HTMLElement],
      default: undefined,
    },
  },

  data: () => ({
    files: [],
    showDragInfo: false,
    fileValid: true,
    chooseModal: false,
    useCamera: false,
    showAltTextModal: [],
    isUploadAnotherFile: false,
  }),

  computed: {
    isAndroid() {
      return this.$store.state.isAndroid
    },

    allowMultiFiles() {
      return this.multiFiles ? 'files[]' : 'files'
    },

    extension() {
      if (this.useCamera) return 'image/*'
      return this.fileTypes.map((el) => `.${el}`).join(',')
    },

    getExtensionRegex() {
      // generate something like /.csv$|.xls$|.xlsx$/i
      if (!this.fileTypes.length) return
      return new RegExp(this.fileTypes.map((el) => `.${el}$`).join('|'), 'i')
    },

    showUploadFileModal: {
      get: function () {
        return !!this.files.length
      },

      set: function () {},
    },
  },

  emits: ['filesChosen', 'close', 'fileValid'],

  methods: {
    async openFileMenu(useCamera = false) {
      this.useCamera = useCamera
      this.chooseModal = false

      await this.$nextTick()

      document.getElementById(this.inputId).value = ''
      document.getElementById(this.inputId).click()
    },

    async uploadAnotherFile() {
      this.isUploadAnotherFile = true
      const isCapacitor = await native.isCapacitorSupported()

      if (!this.isAndroid || !isCapacitor) return this.openFileMenu()

      /*
       * On android (only native app, not in browser), capacitor is not able to show the app chooser.
       * So we need to open a modal to let user choose between camera and gallery
       */
      this.chooseModal = true
    },

    prevent(e) {
      e.stopPropagation()
      e.preventDefault()
    },

    dragEnter(event) {
      if (!this.showDraggingInfo) return this.prevent(event)
      this.showDragInfo = true
      this.prevent(event)
    },

    dragLeave(event) {
      if (!event.screenX && !event.screenY && !this.files?.length) {
        this.showDragInfo = false
      }
      this.prevent(event)
    },

    windowLeave(event) {
      if (event.toElement == null && event.relatedTarget == null) {
        this.showDragInfo = false
      }
    },

    async onPaste(event) {
      const res = await this.asyncHandle(event)
      const filteredRes = res.filter((el) => el)

      if (!filteredRes.length) return
      this.prevent(event)
    },

    async handleFiles(event) {
      this.prevent(event)

      const res = await this.asyncHandle(event)
      const filteredRes = res.filter((el) => el)

      this.showDragInfo = !!filteredRes.length
      this.readingFilesIsFinished()
      this.showDragInfo = false
    },

    async asyncHandle(event) {
      const items =
        event.type === 'paste'
          ? (event.clipboardData || event.originalEvent.clipboardData).items
          : event.target.files || event.dataTransfer.files
      const blob = []
      const promises = []

      if (!items) return []

      for (let i = 0; i < items.length; i++) {
        blob[i] = event.type === 'paste' ? items[i].getAsFile() : items[i]
        promises.push(this.getBase64(blob[i]))
      }

      return Promise.all(promises)
    },

    getBase64(blob) {
      if (!blob) return
      const BLOB_SIZE = blob.size ? Number(this.$getSize(blob.size, false).replace(/\D/g, '')) / 100 : 0
      const MAX_SIZE_IN_MB = 5
      const reader = new FileReader()
      const self = this

      // do not read the file if file is to big
      if (BLOB_SIZE > MAX_SIZE_IN_MB) {
        return new Promise((resolve) => {
          const file = {
            id: Math.round(Math.random() * 9999999),
            name: blob.name,
            value: '',
            size: blob.size,
            can_show_preview: false,
            origin: blob,
          }
          self.setFile(file)
          resolve(file)
        })
      }

      return new Promise((resolve) => {
        reader.onload = (event) => {
          const file = {
            id: Math.round(Math.random() * 9999999),
            name: blob.name,
            value: event.target.result,
            size: blob.size,
            can_show_preview: event.target.result.startsWith('data:image'),
            origin: blob,
          }
          self.setFile(file)
          resolve(file)
        }
        reader.readAsDataURL(blob)
      })
    },

    setFile(file) {
      this.files = [...this.files, file]
    },

    readingFilesIsFinished() {
      if (this.showPreviewBeforeUpload && this.showDragInfo) return
      this.submitAndClose()
    },

    submitAndClose() {
      this.isUploadAnotherFile = false
      this.$emit('filesChosen', this.files)
      this.$emit('close')
      this.validateFile()
      this.files = []
      this.showDragInfo = false
      // reset input files
      const input = document.getElementById(this.inputId)
      if (!input) return
      input.value = ''
    },

    validateFile() {
      if (!this.files || !this.getExtensionRegex) return true
      this.fileValid = this.files.every((file) => this.getExtensionRegex.test(file.name))
      this.$emit('fileValid', this.fileValid)
    },

    cancelUpload() {
      this.files = []
      this.showDragInfo = false
      this.$emit('close')
    },

    deleteFile(file) {
      this.files = this.files.filter((s) => s !== file)
      if (this.files?.length) return
      this.cancelUpload()
    },

    openAltTextModal(fileId) {
      this.showAltTextModal.push(fileId)
    },

    closeAltTextModal(fileId) {
      this.showAltTextModal = this.showAltTextModal.filter((el) => el !== fileId)
    },

    updateAltText(fileId, newAltText) {
      this.files = this.files.map((file) => {
        if (file.id !== fileId) return file
        file.alt_name = newAltText
        return file
      })
      this.closeAltTextModal(fileId)
    },

    isImage(file) {
      if (!file) return false
      return file.can_show_preview || file.type?.startsWith('image')
    },

    clickHandler(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        this.cancelUpload()
      }
    },
  },

  watch: {
    modelValue: {
      handler: async function (newValue) {
        if (!newValue) return

        const isCapacitor = await native.isCapacitorSupported()
        if (!this.isAndroid || !isCapacitor) return this.openFileMenu()

        /*
         * On android (only native app, not in browser), capacitor is not able to show the app chooser (please don't ask me why)
         * So we need to open a modal to let user choose between camera and gallery
         */
        this.chooseModal = true
      },

      immediate: true,
    },
  },

  created() {
    window.addEventListener('paste', this.onPaste)
    window.addEventListener('drop', this.handleFiles)
    window.addEventListener('dragover', this.dragEnter)
    window.addEventListener('dragleave', this.dragLeave)
    window.addEventListener('keydown', this.clickHandler)
    window.addEventListener('mouseout', this.windowLeave)
  },

  unmounted() {
    window.removeEventListener('paste', this.onPaste)
    window.removeEventListener('drop', this.handleFiles)
    window.removeEventListener('dragover', this.dragEnter)
    window.removeEventListener('dragleave', this.dragLeave)
    window.removeEventListener('keydown', this.clickHandler)
    window.removeEventListener('mouseout', this.windowLeave)
  },
}
</script>

<style scoped lang="scss">
.single-file-preview {
  max-width: 348px;
  height: auto;
  max-height: 500px;
  display: block;
  margin: 0 auto;
  border-radius: 8px;

  @include media($mobile) {
    max-height: 260px;
    max-width: 170px;
  }
}

:deep(.sdui-list-item__content) {
  max-width: 80%;
}

.upload-list :deep(.sdui-list-item__title) {
  display: inline;
}

// don't display full titles for files
.overflow-dots,
.upload-list :deep(.sdui-list-item__title) {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.drag-info {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  align-items: center;
  justify-content: space-around;
  display: flex;
  opacity: 0;
  z-index: -10;
}

.file-access__action {
  gap: 10px;
}
</style>

<!--
  Unscoped:
  The Modal is beeing attached to the body (DOM), so :deep() selector does not work anymore (it was working with Vue2).
  So we have to use unscoped styles for modals.
-->
<style lang="scss">
#drop-files-indicator {
  .modal-dialog {
    max-width: calc(100% - 50px) !important;
    width: calc(100% - 50px) !important;
    min-height: calc(100% - 50px) !important;
  }

  .modal-content {
    height: 100%;
    width: 100%;
    position: absolute;
  }

  .inner-info {
    padding: 30px;
    border-radius: 16px;
    transition: 0.2s;
    position: absolute;
    left: 0;
    top: 0;

    .icon {
      font-size: pxtorem(64);
    }
  }
}
</style>
